// Small date helpers shared across the group screens.

/**
 * Given a stored birthday (e.g. "1990-05-15" or an ISO datetime), work out the
 * NEXT occurrence of that birthday from today.
 *
 * Returns null if no birthday, otherwise:
 *   { iso: 'YYYY-MM-DD', date: Date, daysUntil: number, isToday: boolean, label: 'May 15' }
 */
export function nextBirthday(birthday) {
    if (!birthday) return null;

    const parts = String(birthday).slice(0, 10).split('-');
    if (parts.length !== 3) return null;
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    if (!month || !day) return null;

    const today = new Date(new Date().toDateString());
    let candidate = new Date(today.getFullYear(), month - 1, day);
    if (candidate < today) {
        candidate = new Date(today.getFullYear() + 1, month - 1, day);
    }

    const daysUntil = Math.round((candidate - today) / (1000 * 60 * 60 * 24));
    const iso = `${candidate.getFullYear()}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const label = candidate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

    return { iso, date: candidate, daysUntil, isToday: daysUntil === 0, label };
}

/** "in 3 days" / "Today!" / "in 1 day" */
export function countdownLabel(daysUntil) {
    if (daysUntil === 0) return 'Today!';
    return `in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`;
}

/**
 * Format a date-only string ("YYYY-MM-DD" or an ISO datetime at UTC midnight)
 * WITHOUT the timezone shift you get from `new Date("2026-10-17")`, which parses
 * as UTC and can display a day early in western timezones.
 */
export function formatDateOnly(dateStr) {
    if (!dateStr) return '';
    const [y, m, d] = String(dateStr).slice(0, 10).split('-').map((n) => parseInt(n, 10));
    if (!y || !m || !d) return '';
    return new Date(y, m - 1, d).toLocaleDateString();
}

/**
 * Default reveal date for a gift: the DAY AFTER the recipient's next birthday,
 * so the surprise lasts through their birthday and they see who gave what the
 * next day (for thank-yous). Returns null if no birthday.
 */
export function revealAfterBirthday(birthday) {
    const b = nextBirthday(birthday);
    if (!b) return null;
    const d = new Date(b.date);
    d.setDate(d.getDate() + 1); // day after
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return { iso, label, birthdayLabel: b.label };
}
